import type { ComponentPropsWithRef, ReactNode } from 'react'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'

export type ButtonColor =
    | 'light'
    | 'dark'
    | 'primary'
    | 'info'
    | 'warning'
    | 'danger'
    | 'outline'
    | 'success'
    | 'danger-outline'
    | 'dark-outline'
    | 'info-outline'

export type ButtonSize = 'normal' | 'small'

interface ButtonBaseProps {
    color?: ButtonColor
    size?: ButtonSize
    loading?: boolean
    disabled?: boolean
    loadingText?: string
    iconName?: IconNameEnum
    iconClass?: string
    iconRight?: boolean
    useBookingFormThemeCssVariables?: boolean
    children?: ReactNode
    className?: string
}

type ButtonAsButtonProps = ButtonBaseProps &
    Omit<ComponentPropsWithRef<'button'>, keyof ButtonBaseProps> & {
        as?: 'button'
    }

type ButtonAsAnchorProps = ButtonBaseProps &
    Omit<ComponentPropsWithRef<'a'>, keyof ButtonBaseProps> & {
        as: 'a'
    }

export type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps

function getIconClasses(size: ButtonSize, iconClass?: string): string {
    if (iconClass) {
        return iconClass
    }

    if (size === 'small') {
        return 'w-3 h-3'
    }

    return 'w-4 h-4'
}

function getSizeClasses(size: ButtonSize): string {
    if (size === 'small') {
        return 'px-3 py-1.5 text-xs h-8 rounded-lg'
    }

    return 'px-4 py-2.5 text-sm h-10.5 rounded-lg2'
}

function getCursorClasses(isDisabledOrLoading: boolean): string {
    if (isDisabledOrLoading) {
        return 'cursor-not-allowed'
    }

    return 'cursor-pointer'
}

function getColorClasses(
    color: ButtonColor,
    isDisabledOrLoading: boolean,
    useBookingFormThemeCssVariables: boolean,
): string {
    if (isDisabledOrLoading) {
        return 'bg-stone-200 text-gray-500 cursor-default border border-transparent'
    }

    if (useBookingFormThemeCssVariables) {
        return 'bg-booking-form-theme-main-background-color hover:bg-booking-form-theme-main-background-color-90 text-booking-form-theme-main-text-shade'
    }

    switch (color) {
        case 'danger':
            return 'bg-red-600 hover:bg-red-700 text-white border border-red-600'
        case 'danger-outline':
            return 'bg-white text-red-600 border border-stone-200 hover:bg-red-600 hover:text-white'
        case 'light':
            return 'bg-white border border-stone-200 hover:bg-stone-100 text-gray-700'
        case 'dark':
            return 'bg-gray-800 border border-gray-700 hover:bg-gray-900 text-gray-50'
        case 'dark-outline':
            return 'bg-white text-gray-700 border border-stone-200 hover:bg-gray-900 hover:text-white'
        case 'success':
            return 'bg-green-600 hover:bg-green-700 text-white'
        case 'warning':
            return 'bg-yellow-500 hover:bg-yellow-400 text-black border border-yellow-500'
        case 'info':
            return 'bg-blue-500 hover:bg-blue-600 text-white border border-blue-500'
        case 'info-outline':
            return 'bg-white hover:bg-blue-500 text-blue-500 hover:text-white border border-blue-500'
        case 'outline':
            return 'bg-white text-primary border border-stone-200 hover:bg-primary hover:text-white'
        case 'primary':
            return 'bg-primary hover:bg-primary-shade text-primary-content border border-primary'
    }
}

export function Button(props: ButtonProps) {
    const {
        as = 'button',
        color = 'dark',
        size = 'normal',
        loading = false,
        disabled = false,
        loadingText,
        iconName,
        iconClass,
        iconRight = false,
        useBookingFormThemeCssVariables = false,
        children,
        className = '',
    } = props

    const isDisabledOrLoading = loading || disabled
    const iconClasses = getIconClasses(size, iconClass)
    const sizeClasses = getSizeClasses(size)
    const cursorClasses = getCursorClasses(isDisabledOrLoading)
    const colorClasses = getColorClasses(
        color,
        isDisabledOrLoading,
        useBookingFormThemeCssVariables,
    )

    const baseClasses =
        'tracking-wide shadow-xs uppercase font-bold flex items-center justify-center gap-2 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary focus-visible:outline-none'
    const combinedClassName =
        `${baseClasses} ${colorClasses} ${sizeClasses} ${cursorClasses} ${className}`.trim()

    const content = (
        <>
            {loading ? (
                <Icon
                    name={IconNameEnum.LoadingSpinner}
                    className={`animate-spin ${iconClasses}`}
                    aria-hidden="true"
                />
            ) : null}

            {iconName && !loading && !iconRight ? (
                <Icon
                    name={iconName}
                    className={iconClasses}
                    aria-hidden="true"
                />
            ) : null}

            {loading && loadingText ? (
                <div>{loadingText}</div>
            ) : children ? (
                <div className="whitespace-nowrap">{children}</div>
            ) : null}

            {iconName && !loading && iconRight ? (
                <Icon
                    name={iconName}
                    className={iconClasses}
                    aria-hidden="true"
                />
            ) : null}
        </>
    )

    if (as === 'a') {
        const {
            as: _as,
            color: _color,
            size: _size,
            loading: _loading,
            disabled: _disabled,
            loadingText: _loadingText,
            iconName: _iconName,
            iconClass: _iconClass,
            iconRight: _iconRight,
            useBookingFormThemeCssVariables: _useBookingFormThemeCssVariables,
            children: _children,
            className: _className,
            tabIndex,
            onClick,
            ...anchorProps
        } = props as ButtonAsAnchorProps

        const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (isDisabledOrLoading) {
                e.preventDefault()
                e.stopPropagation()
                return
            }
            onClick?.(e)
        }

        return (
            <a
                role="link"
                aria-disabled={isDisabledOrLoading ? 'true' : undefined}
                tabIndex={isDisabledOrLoading ? -1 : tabIndex}
                onClick={handleAnchorClick}
                {...anchorProps}
                className={combinedClassName}
            >
                {content}
            </a>
        )
    }

    const {
        as: _as,
        color: _color,
        size: _size,
        loading: _loading,
        disabled: _disabled,
        loadingText: _loadingText,
        iconName: _iconName,
        iconClass: _iconClass,
        iconRight: _iconRight,
        useBookingFormThemeCssVariables: _useBookingFormThemeCssVariables,
        children: _children,
        className: _className,
        type = 'button',
        ...buttonProps
    } = props as ButtonAsButtonProps

    return (
        <button
            {...buttonProps}
            type={type}
            disabled={isDisabledOrLoading}
            aria-busy={loading ? 'true' : undefined}
            className={combinedClassName}
        >
            {content}
        </button>
    )
}
